import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { marked } from "marked";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { downloadPdfWithProgress } from "@/lib/pdfDownload";
import ConsultingBreadcrumbs from "@/components/ConsultingBreadcrumbs";
import WysiwygMarkdownEditor from "@/components/WysiwygMarkdownEditor";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  Copy,
  RefreshCw,
  Save,
  History,
  Edit3,
  Check,
  Trash2,
  Download,
  StickyNote,
  Share2,
  X,
  Mail,
  Link2,
  Send,
  ShieldCheck,
  Clock,
  FileText,
  Wand2,
} from "lucide-react";

// ==========================================================================
// Configuración de formularios por herramienta
// ==========================================================================

const TOOL_FORMS = {
  job_offer: {
    title: "Generador de ofertas de empleo",
    fields: [
      { name: "puesto", label: "Nombre del puesto", type: "text", placeholder: "Ej. Product Designer Senior", required: true },
      { name: "enfoque", label: "Enfoque especial (opcional)", type: "text", placeholder: "Ej. tono más técnico, orientado a growth, remoto internacional…" },
    ],
    cta: "Generar oferta",
  },
  competency_profile: {
    title: "Perfil de competencias",
    fields: [
      { name: "puesto", label: "Puesto a definir", type: "text", placeholder: "Ej. Data Engineer", required: true },
      { name: "contexto", label: "Contexto del puesto (opcional)", type: "textarea", placeholder: "Ej. va a liderar la migración a Snowflake y mentorizar a 1 junior.", rows: 3 },
    ],
    cta: "Generar perfil",
  },
  interview_assistant: {
    title: "Asistente de entrevista por competencias",
    fields: [
      { name: "puesto", label: "Puesto a entrevistar", type: "text", placeholder: "Ej. Backend Engineer", required: true },
    ],
    cta: "Generar guía de entrevista",
  },
  onboarding_plan: {
    title: "Plan de onboarding personalizado",
    fields: [
      { name: "nombre", label: "Nombre del empleado", type: "text", placeholder: "Ej. Ana López", required: true },
      { name: "puesto", label: "Puesto", type: "text", placeholder: "Ej. Marketing Manager", required: true },
      { name: "fecha_inicio", label: "Fecha de inicio", type: "date", required: true },
    ],
    cta: "Generar plan de onboarding",
  },
  evaluation: {
    title: "Asistente de evaluación",
    fields: [
      { name: "empleado", label: "Nombre del empleado", type: "text", placeholder: "Ej. Carlos Ruiz", required: true },
      { name: "rol", label: "Rol", type: "text", placeholder: "Ej. Tech Lead" },
      { name: "scores_text", label: "Puntuaciones por criterio (uno por línea, formato `Criterio: N/5`)", type: "textarea", rows: 5, placeholder: "Comunicación: 4/5\nOwnership: 3/5\nExecución: 4/5" },
      { name: "notas", label: "Notas del mánager (opcional)", type: "textarea", rows: 3 },
    ],
    cta: "Generar evaluación",
  },
  feedback: {
    title: "Feedback estructurado",
    fields: [
      { name: "tipo", label: "Tipo de feedback", type: "select", required: true, options: [
        { value: "positivo", label: "Positivo" },
        { value: "mejora", label: "De mejora" },
        { value: "mixto", label: "Mixto" },
      ]},
      { name: "tono", label: "Tono", type: "select", required: true, options: [
        { value: "equilibrado", label: "Equilibrado" },
        { value: "directo", label: "Directo" },
        { value: "empatico", label: "Empático" },
      ]},
      { name: "corto", label: "Solo versión breve (conversación verbal)", type: "checkbox" },
      { name: "situacion", label: "Describe la situación en lenguaje libre", type: "textarea", rows: 5, required: true, placeholder: "Ej. María entregó tarde 3 sprints y no lo comunicó hasta el review…" },
    ],
    cta: "Generar feedback",
  },
  development_plan: {
    title: "Plan de desarrollo individual",
    fields: [
      { name: "empleado", label: "Nombre del empleado", type: "text", required: true },
      { name: "nivel_actual", label: "Nivel profesional actual", type: "text", placeholder: "Ej. Senior · nivel 3", required: true },
      { name: "objetivos", label: "Objetivos declarados", type: "textarea", rows: 3, placeholder: "Ej. Liderar un proyecto crítico este trimestre, mentorizar a 2 juniors" },
      { name: "puntos_mejora", label: "Puntos de mejora", type: "textarea", rows: 3, placeholder: "Ej. Comunicación asíncrona, priorización" },
    ],
    cta: "Generar plan de desarrollo",
  },
};

// Metadatos técnicos por herramienta (tira output + tiempo, empty state, ejemplos)
const TOOL_META = {
  job_offer: {
    time: "~40 s",
    output: "Markdown editable · Copy-paste directo",
    kicker: "Oferta de empleo",
    preview: "Aquí aparecerá una oferta de 4-6 secciones con hook cultural, propuesta de valor (EVP), responsabilidades, requisitos y proceso — todo alineado con tu diagnóstico.",
    examples: [
      { label: "Product Designer Senior", inputs: { puesto: "Product Designer Senior", enfoque: "remoto internacional, tono cercano" } },
      { label: "Backend Engineer Junior", inputs: { puesto: "Backend Engineer Junior", enfoque: "posición para crecer, tono orientado a aprendizaje" } },
      { label: "Data Analyst", inputs: { puesto: "Data Analyst", enfoque: "roadmap de datos claro y ownership desde día uno" } },
    ],
  },
  competency_profile: {
    time: "~50 s",
    output: "Markdown estructurado · PDF-listo",
    kicker: "Perfil de competencias",
    preview: "Verás un perfil por competencias técnicas, conductuales y culturales con niveles esperados, indicadores conductuales y sugerencias de evaluación.",
    examples: [
      { label: "Data Engineer", inputs: { puesto: "Data Engineer", contexto: "va a liderar la migración a Snowflake y mentorizar a 1 junior." } },
      { label: "Head of Sales", inputs: { puesto: "Head of Sales", contexto: "responsable de definir estrategia comercial B2B y liderar equipo de 4 personas." } },
    ],
  },
  interview_assistant: {
    time: "Guía interactiva",
    output: "Preguntas + scoring 1-3-5 + resumen",
    kicker: "Guía de entrevista",
    preview: "Se generará una guía estructurada por competencias con 3-5 preguntas por área, criterios de scoring (1-3-5) y una plantilla de resumen final del candidato.",
    examples: [
      { label: "Backend Engineer", inputs: { puesto: "Backend Engineer" } },
      { label: "Product Manager", inputs: { puesto: "Product Manager" } },
    ],
  },
  onboarding_plan: {
    time: "~45 s",
    output: "Plan 30/60/90 · Responsables · Check-ins",
    kicker: "Plan de onboarding",
    preview: "Aparecerá un plan personalizado dividido en semanas 1, mes 1, mes 2 y mes 3 con acciones concretas, responsables, materiales y check-ins.",
    examples: [
      { label: "Ana López · Marketing Manager", inputs: { nombre: "Ana López", puesto: "Marketing Manager", fecha_inicio: "" } },
    ],
  },
  evaluation: {
    time: "~35 s",
    output: "Feedback listo para 1:1 · Markdown",
    kicker: "Evaluación de desempeño",
    preview: "Convertiremos tus puntuaciones por criterio en un borrador de conversación de evaluación estructurada, con fortalezas, áreas de mejora y compromisos.",
    examples: [
      {
        label: "Carlos · Tech Lead",
        inputs: { empleado: "Carlos Ruiz", rol: "Tech Lead", scores_text: "Comunicación: 4/5\nOwnership: 3/5\nExecución: 4/5", notas: "Excelente sprint, cuesta pedir ayuda a tiempo." },
      },
    ],
  },
  feedback: {
    time: "~30 s",
    output: "Feedback estructurado · Reutilizable",
    kicker: "Feedback estructurado",
    preview: "Verás un feedback estructurado según el modelo definido en tu diagnóstico (SBI, ABC, radical candor…) listo para conversación o email.",
    examples: [
      {
        label: "María · sprints retrasados",
        inputs: { tipo: "mejora", tono: "empatico", situacion: "María entregó tarde 3 sprints seguidos y no lo comunicó hasta el review. Es senior en el equipo." },
      },
    ],
  },
  development_plan: {
    time: "~50 s",
    output: "Objetivos, recursos y criterios de nivel",
    kicker: "Plan de desarrollo",
    preview: "Aparecerá un plan de crecimiento con objetivos SMART, acciones concretas, recursos recomendados y criterios explícitos para promocionar al siguiente nivel.",
    examples: [
      {
        label: "Ana · Senior nivel 3",
        inputs: {
          empleado: "Ana López",
          nivel_actual: "Senior · nivel 3",
          objetivos: "Liderar un proyecto crítico este trimestre, mentorizar a 2 juniors.",
          puntos_mejora: "Comunicación asíncrona, priorización.",
        },
      },
    ],
  },
};

export default function ConsultingToolPage() {
  const { testId, toolKey } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const runIdParam = searchParams.get("run");
  const cfg = TOOL_FORMS[toolKey];

  const [inputs, setInputs] = useState({});
  const [generating, setGenerating] = useState(false);
  const [run, setRun] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [test, setTest] = useState(null); // { company_name, report_ready_at }
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareEmails, setShareEmails] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [shareSending, setShareSending] = useState(false);
  const [shareLoadingUrl, setShareLoadingUrl] = useState(false);
  const pollRef = useRef(null);
  const meta = TOOL_META[toolKey] || {};

  useEffect(() => {
    loadHistory();
    // Contexto de empresa/informe para el hero
    api.get(`/consulting/tests/${testId}`).then((r) => setTest(r.data)).catch(() => {});
    if (runIdParam) loadRun(runIdParam);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolKey, testId]);

  async function loadHistory() {
    setLoadingHistory(true);
    try {
      const r = await api.get(`/consulting/tests/${testId}/tools/${toolKey}/history`);
      setHistory(r.data.items || []);
    } catch {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  }

  async function loadRun(runId, { updateUrl = true, keepGenerating = false } = {}) {
    try {
      const r = await api.get(`/consulting/tool_runs/${runId}`);
      setRun(r.data);
      setInputs(r.data.inputs || {});
      setNotesDraft(r.data.notes || "");
      setTitleDraft(r.data.title || r.data.inputs?.puesto || "");
      setShareUrl(""); // reset — se genera on-demand
      if (updateUrl) setSearchParams({ run: runId }, { replace: true });
      if (r.data.run_status === "running") {
        pollRef.current = setTimeout(() => loadRun(runId, { updateUrl: false, keepGenerating: true }), 3000);
        setGenerating(true);
      } else {
        setGenerating(false);
        if (r.data.run_status === "failed") {
          toast.error(`Falló: ${r.data.error || "error desconocido"}`);
        } else if (keepGenerating) {
          toast.success("Generado con éxito");
          loadHistory();
        }
      }
    } catch {
      toast.error("No se encontró la generación");
      setGenerating(false);
    }
  }

  function applyExample(ex) {
    setInputs((prev) => ({ ...prev, ...ex.inputs }));
    toast.success(`Ejemplo cargado: ${ex.label}`);
  }

  async function saveNotes() {
    if (!run?.run_id) return;
    setSavingNotes(true);
    try {
      await api.patch(`/consulting/tool_runs/${run.run_id}`, { notes: notesDraft });
      setRun({ ...run, notes: notesDraft });
      toast.success("Notas guardadas");
      setNotesOpen(false);
    } catch {
      toast.error("No pudimos guardar las notas");
    } finally {
      setSavingNotes(false);
    }
  }

  async function saveTitle() {
    if (!run?.run_id) return;
    const title = titleDraft.trim();
    if (!title) return;
    try {
      await api.patch(`/consulting/tool_runs/${run.run_id}`, { title });
      setRun({ ...run, title });
      // Refresca history para reflejar el nuevo título
      loadHistory();
      setRenaming(false);
      toast.success("Título actualizado");
    } catch {
      toast.error("No pudimos renombrar");
    }
  }

  async function downloadRunPdf() {
    if (!run?.run_id) return;
    setDownloadingPdf(true);
    try {
      const safe = (run.title || "output").replace(/\s+/g, "_").slice(0, 60);
      await downloadPdfWithProgress({
        url: `/consulting/tool_runs/${run.run_id}/pdf`,
        filename: `${toolKey}_${safe}.pdf`,
        kind: "consulting-tool",
        label: "Preparando tu recurso operativo",
        variant: "premium",
        timeout: 180000,
      });
      toast.success("PDF descargado", {
        description: "¿Compartirlo con el equipo?",
        duration: 5500,
        action: { label: "Compartir", onClick: () => openShare() },
      });
    } catch (err) {
      toast.error("No pudimos generar el PDF");
    } finally {
      setDownloadingPdf(false);
    }
  }

  async function openShare() {
    if (!run?.run_id) return;
    setShareOpen(true);
    if (shareUrl) return;
    setShareLoadingUrl(true);
    try {
      const { data } = await api.post(`/consulting/tool_runs/${run.run_id}/share`);
      setShareUrl(data.public_url || "");
    } catch {
      toast.error("No pudimos generar el link");
    } finally {
      setShareLoadingUrl(false);
    }
  }

  function copyShareUrl() {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success("Link copiado"),
      () => toast.error("No pudimos copiar")
    );
  }

  async function sendShareEmail() {
    const emails = shareEmails.split(/[,;\n\s]+/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) return toast.error("Añade al menos un email");
    if (emails.length > 5) return toast.error("Máximo 5 emails");
    setShareSending(true);
    try {
      const { data } = await api.post(`/consulting/tool_runs/${run.run_id}/share/email`, {
        emails,
        message: shareMessage || null,
      });
      const sent = (data.sent || []).length;
      const failed = (data.failed || []).length;
      if (sent > 0) toast.success(`Enviado a ${sent} destinatario${sent > 1 ? "s" : ""}`);
      if (failed > 0) toast.error(`Fallaron ${failed}`);
      if (sent > 0 && failed === 0) {
        setShareEmails("");
        setShareMessage("");
        setShareOpen(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar los emails");
    } finally {
      setShareSending(false);
    }
  }

  function updateField(name, value) {
    setInputs((prev) => ({ ...prev, [name]: value }));
  }

  function serializeInputs() {
    // Post-procesa scores_text → scores object si aplica
    const out = { ...inputs };
    if (toolKey === "evaluation" && out.scores_text) {
      const scores = {};
      out.scores_text.split("\n").forEach((line) => {
        const m = line.match(/^(.+?):\s*(\d+(?:\.\d+)?)/);
        if (m) scores[m[1].trim()] = parseFloat(m[2]);
      });
      out.scores = scores;
      delete out.scores_text;
    }
    return out;
  }

  async function generate() {
    const missing = (cfg.fields || []).filter((f) => f.required && !inputs[f.name]);
    if (missing.length) {
      toast.error(`Falta: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post(`/consulting/tests/${testId}/tools/${toolKey}/generate`, {
        inputs: serializeInputs(),
      });
      // La respuesta llega en ~1s con run_status="running" y run_id.
      // Empezamos a hacer polling.
      loadRun(r.data.run_id, { updateUrl: true, keepGenerating: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "No pudimos generar";
      toast.error(msg);
      setGenerating(false);
    }
  }

  const displayedMd = useMemo(() => run?.edited_output_md ?? run?.output_md ?? "", [run]);

  function copyToClipboard() {
    navigator.clipboard.writeText(displayedMd).then(
      () => toast.success("Copiado al portapapeles"),
      () => toast.error("No pudimos copiar")
    );
  }

  function startEdit() {
    setEditDraft(displayedMd);
    setEditing(true);
  }

  async function saveEdit() {
    try {
      await api.patch(`/consulting/tool_runs/${run.run_id}`, { edited_output_md: editDraft });
      setRun({ ...run, edited_output_md: editDraft });
      setEditing(false);
      toast.success("Cambios guardados");
    } catch {
      toast.error("No pudimos guardar");
    }
  }

  async function removeRun(runId) {
    if (!window.confirm("¿Borrar esta generación?")) return;
    try {
      await api.delete(`/consulting/tool_runs/${runId}`);
      if (run?.run_id === runId) {
        setRun(null);
        setSearchParams({}, { replace: true });
      }
      loadHistory();
      toast.success("Borrado");
    } catch {
      toast.error("No pudimos borrar");
    }
  }

  if (!cfg) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)" }}
      >
        <div className="text-center">
          <p className="text-slate-500 mb-4">Herramienta desconocida</p>
          <button onClick={() => navigate(`/consulting/${testId}/tools`)} className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: "#0F6E56" }}>
            Volver a las herramientas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-8 md:py-12 px-4 md:px-6 overflow-x-hidden"
      style={{
        background:
          "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
          "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
          "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
      }}
      data-testid={`consulting-tool-${toolKey}`}
    >
      <div className="max-w-4xl mx-auto">
        <ConsultingBreadcrumbs
          crumbs={[
            { label: "Consultorías", to: "/consulting" },
            { label: test?.nickname || test?.company_name || "Consultoría", to: `/consulting/${testId}/report` },
            { label: "Herramientas", to: `/consulting/${testId}/tools` },
            { label: meta.kicker || toolKey },
          ]}
        />
        <button
          onClick={() => navigate(`/consulting/${testId}/tools`)}
          className="text-xs font-semibold text-slate-500 hover:text-[#0F6E56] inline-flex items-center gap-1.5 mb-8 transition-colors group"
          data-testid="consulting-tool-back"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" /> Volver a las herramientas
        </button>

        {/* Hero premium */}
        <div
          className="relative rounded-3xl p-8 mb-8 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
            boxShadow: "0 30px 60px -30px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.15) inset",
          }}
        >
          <span aria-hidden className="absolute -top-8 -right-8 pointer-events-none" style={{ width: 220, height: 220, background: "radial-gradient(circle at 100% 0%, rgba(201,168,76,0.35) 0%, rgba(201,168,76,0) 65%),repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 10px)" }} />
          <span aria-hidden className="absolute bottom-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />
          <div className="relative flex items-start gap-5 flex-wrap">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", boxShadow: "0 8px 22px -6px rgba(201,168,76,0.55)" }}
            >
              <Sparkles className="w-7 h-7" style={{ color: "#0b3d2e" }} strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold mb-3 px-3 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.15)", color: "#E5C874" }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                Recurso operativo
              </div>
              <h1 className="font-display text-3xl font-bold text-white leading-tight" style={{ letterSpacing: "-0.02em" }}>
                {cfg.title}
                {test?.company_name && (
                  <>
                    {" · "}
                    <span
                      style={{
                        background: "linear-gradient(90deg, #E5C874 0%, #C9A84C 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {test.company_name}
                    </span>
                  </>
                )}
              </h1>
              {(meta.time || meta.output) && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs md:text-sm" style={{ color: "rgba(255,255,255,0.78)" }}>
                  {meta.time && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" style={{ color: "#E5C874" }} /> {meta.time}
                    </span>
                  )}
                  {meta.output && (
                    <>
                      <span className="hidden sm:inline text-white/30">·</span>
                      <span className="inline-flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" style={{ color: "#E5C874" }} /> {meta.output}
                      </span>
                    </>
                  )}
                </div>
              )}
              {test?.report_ready_at && (
                <div className="mt-4 pt-3 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ borderTop: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}>
                  <span className="inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" style={{ color: "#C9A84C" }} />
                    Basado en tu informe del {new Date(test.report_ready_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
                  </span>
                  <span className="text-white/25">·</span>
                  <span>Consultoría OptimizaRRHH</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div
          className="rounded-3xl p-7 mb-6"
          style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(11,61,46,0.06)" }}
          data-testid="consulting-tool-form"
        >
          <div className="flex items-center gap-3 mb-5">
            <span style={{ width: 24, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#0b3d2e" }}>Configuración</div>
          </div>
          {/* Example prompts pills */}
          {(meta.examples || []).length > 0 && !run && (
            <div className="mb-5" data-testid="consulting-tool-examples">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2 flex items-center gap-1.5" style={{ color: "#94a3b8" }}>
                <Wand2 className="w-3 h-3" style={{ color: "#C9A84C" }} strokeWidth={2.5} /> Prueba con un ejemplo
              </div>
              <div className="flex flex-wrap gap-2">
                {meta.examples.map((ex, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => applyExample(ex)}
                    className="rounded-full px-3 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                    style={{
                      background: "linear-gradient(135deg, rgba(201,168,76,0.10) 0%, rgba(15,110,86,0.06) 100%)",
                      border: "1px solid rgba(201,168,76,0.25)",
                      color: "#0b3d2e",
                    }}
                    data-testid={`consulting-tool-example-${i}`}
                  >
                    <Sparkles className="w-3 h-3" style={{ color: "#C9A84C" }} strokeWidth={2.5} />
                    {ex.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4">
            {(cfg.fields || []).map((f) => (
              <FormField key={f.name} field={f} value={inputs[f.name] || ""} onChange={(v) => updateField(f.name, v)} />
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={generate}
              disabled={generating}
              className="group relative inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white overflow-hidden transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)", boxShadow: "0 8px 22px -8px rgba(11,61,46,0.55), 0 0 0 1px rgba(201, 168, 76, 0.25) inset", letterSpacing: "0.02em" }}
              data-testid="consulting-tool-generate"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" style={{ color: "#C9A84C" }} strokeWidth={2.5} />}
              <span className="relative">{generating ? "Generando…" : cfg.cta}</span>
            </button>
            {run && (
              <button
                onClick={generate}
                disabled={generating}
                className="rounded-full px-5 py-2.5 text-sm font-semibold text-slate-700 inline-flex items-center gap-2 disabled:opacity-50 transition-all hover:-translate-y-0.5"
                style={{ background: "#ffffff", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px -6px rgba(0,0,0,0.1)" }}
                data-testid="consulting-tool-regenerate"
              >
                <RefreshCw className={`w-4 h-4 ${generating ? "animate-spin" : ""}`} /> Regenerar
              </button>
            )}
          </div>
        </div>

        {/* Empty state antes de la primera generación */}
        {!run && !generating && meta.preview && (
          <div
            className="rounded-3xl p-8 mb-6 relative overflow-hidden text-center"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #faf9f0 100%)",
              boxShadow: "0 10px 30px -18px rgba(11,61,46,0.15), 0 0 0 1px rgba(11,61,46,0.05)",
            }}
            data-testid="consulting-tool-empty-preview"
          >
            <div
              className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4"
              style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.12) 0%, rgba(15,110,86,0.08) 100%)" }}
            >
              <FileText className="w-7 h-7" style={{ color: "#C9A84C" }} strokeWidth={2} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2" style={{ color: "#0b3d2e" }}>
              Aún no has generado nada
            </div>
            <p className="text-sm text-slate-500 max-w-lg mx-auto leading-relaxed">
              {meta.preview}
            </p>
            {/* Placeholder líneas skeleton grises */}
            <div className="max-w-md mx-auto mt-6 space-y-2 opacity-60">
              <div className="skeleton-pulse h-3 w-full rounded" />
              <div className="skeleton-pulse h-3 w-11/12 rounded" />
              <div className="skeleton-pulse h-3 w-4/5 rounded" />
              <div className="skeleton-pulse h-3 w-3/4 rounded" />
            </div>
          </div>
        )}

        {/* Output */}
        {run && (
          <div
            className="rounded-3xl p-6 md:p-7 mb-6"
            style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(11,61,46,0.06)" }}
            data-testid="consulting-tool-output"
          >
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold px-3 py-1.5 rounded-full mb-3" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", color: "#E5C874", boxShadow: "0 4px 12px -4px rgba(11,61,46,0.35)" }}>
                  <span style={{ width: 5, height: 5, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                  {run.run_status === "running" ? "Generando…" : run.run_status === "failed" ? "Error" : `Resultado ${run.edited_output_md ? "· editado" : ""}`}
                </div>
                {/* Título editable */}
                {run.run_status === "ready" && (
                  renaming ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={titleDraft}
                        onChange={(e) => setTitleDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setRenaming(false); }}
                        autoFocus
                        className="flex-1 min-w-0 rounded-lg px-3 py-1.5 text-lg font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/40"
                        style={{ border: "1px solid #e2e8f0" }}
                        data-testid="consulting-tool-title-input"
                      />
                      <button onClick={saveTitle} className="rounded-full p-2 text-white" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }} data-testid="consulting-tool-title-save"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setRenaming(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group">
                      <h2 className="font-display text-xl md:text-2xl font-bold text-slate-900 truncate" style={{ letterSpacing: "-0.015em" }} data-testid="consulting-tool-run-title">
                        {run.title || run.inputs?.puesto || "Sin título"}
                      </h2>
                      <button
                        onClick={() => setRenaming(true)}
                        className="rounded-full p-1.5 text-slate-400 hover:text-[#0F6E56] hover:bg-[#0F6E56]/8 opacity-0 group-hover:opacity-100 transition"
                        aria-label="Renombrar"
                        data-testid="consulting-tool-rename"
                      >
                        <Edit3 className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Sticky action bar */}
            {run.run_status === "ready" && (
              <div
                className="sticky top-2 z-20 -mx-2 mb-4 px-2"
                data-testid="consulting-tool-actions-bar"
              >
                <div
                  className="flex items-center gap-1.5 flex-wrap rounded-full px-2 py-1.5"
                  style={{
                    background: "rgba(255,255,255,0.85)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(11,61,46,0.08)",
                    boxShadow: "0 6px 18px -10px rgba(11,61,46,0.28)",
                  }}
                >
                  <button onClick={copyToClipboard} className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1 transition" data-testid="consulting-tool-copy">
                    <Copy className="w-3.5 h-3.5" /> Copiar
                  </button>
                  {!editing ? (
                    <button onClick={startEdit} className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1 transition" data-testid="consulting-tool-edit">
                      <Edit3 className="w-3.5 h-3.5" /> Editar
                    </button>
                  ) : (
                    <button onClick={saveEdit} className="rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 transition" style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", color: "#0b3d2e" }} data-testid="consulting-tool-save-edit">
                      <Save className="w-3.5 h-3.5" strokeWidth={2.5} /> Guardar
                    </button>
                  )}
                  <button onClick={downloadRunPdf} disabled={downloadingPdf} className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1 transition disabled:opacity-50" data-testid="consulting-tool-download-pdf">
                    {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {downloadingPdf ? "Generando…" : "PDF"}
                  </button>
                  <button
                    onClick={() => setNotesOpen((v) => !v)}
                    className="rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 transition"
                    style={{
                      background: (run.notes || "").trim() ? "rgba(201,168,76,0.15)" : "transparent",
                      color: (run.notes || "").trim() ? "#0b3d2e" : "#334155",
                    }}
                    data-testid="consulting-tool-notes-toggle"
                  >
                    <StickyNote className="w-3.5 h-3.5" style={{ color: (run.notes || "").trim() ? "#C9A84C" : undefined }} />
                    Notas {(run.notes || "").trim() && <span className="w-1.5 h-1.5 rounded-full ml-0.5" style={{ background: "#C9A84C" }} />}
                  </button>
                  <button onClick={openShare} className="rounded-full px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 transition" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }} data-testid="consulting-tool-share">
                    <Share2 className="w-3.5 h-3.5" /> Compartir
                  </button>
                </div>
              </div>
            )}

            {/* Panel de notas */}
            {notesOpen && run.run_status === "ready" && (
              <div className="mb-4 rounded-2xl p-4" style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.06) 0%, rgba(15,110,86,0.04) 100%)", border: "1px solid rgba(201,168,76,0.28)" }} data-testid="consulting-tool-notes-panel">
                <div className="flex items-center gap-2 mb-2">
                  <StickyNote className="w-4 h-4" style={{ color: "#C9A84C" }} strokeWidth={2.25} />
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: "#0b3d2e" }}>Notas privadas</div>
                  <span className="text-[10px] text-slate-400 ml-auto">Solo tú puedes verlas · aparecen en el PDF</span>
                </div>
                <textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={3}
                  placeholder="Ej. Usado para 1:1 del viernes con Ana. Falta ajustar la sección de compromisos."
                  className="w-full rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/40"
                  style={{ border: "1px solid #e2e8f0" }}
                  data-testid="consulting-tool-notes-textarea"
                />
                <div className="mt-2 flex gap-2 justify-end">
                  <button onClick={() => { setNotesDraft(run.notes || ""); setNotesOpen(false); }} className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 transition">Cancelar</button>
                  <button onClick={saveNotes} disabled={savingNotes} className="rounded-full px-4 py-1.5 text-xs font-bold text-white inline-flex items-center gap-1.5 transition-all hover:-translate-y-0.5 disabled:opacity-50" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }} data-testid="consulting-tool-notes-save">
                    {savingNotes ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar notas
                  </button>
                </div>
              </div>
            )}

            {run.run_status === "running" ? (
              <div className="py-16 text-center">
                <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: "#0F6E56" }} />
                <p className="text-sm text-slate-600 font-medium">Estamos redactando tu resultado personalizado.</p>
                <p className="text-xs text-slate-400 mt-2">Puede tardar hasta 2 minutos.</p>
              </div>
            ) : run.run_status === "failed" ? (
              <div className="py-8 text-center">
                <p className="text-sm text-rose-600 mb-3">{run.error || "Fallo desconocido"}</p>
                <button onClick={generate} className="rounded-full px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", boxShadow: "0 6px 18px -8px rgba(11,61,46,0.5)" }}>
                  <RefreshCw className="w-4 h-4" /> Reintentar
                </button>
              </div>
            ) : editing ? (
              <div>
                <div className="mb-3 flex items-center gap-2 text-[11px] text-slate-500">
                  <span className="inline-flex w-6 h-6 rounded-full bg-[#0F6E56]/10 items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0F6E56]" />
                  </span>
                  <span>Editando en vista real. Haz clic sobre cualquier texto o celda para modificarlo, luego pulsa <strong>Guardar</strong>.</span>
                </div>
                <WysiwygMarkdownEditor
                  key={run.run_id}
                  initialMd={editDraft}
                  onChange={setEditDraft}
                  testid="consulting-tool-wysiwyg"
                />
              </div>
            ) : (
              <div
                className="prose prose-slate max-w-none consulting-md"
                dangerouslySetInnerHTML={{ __html: marked.parse(displayedMd || "", { async: false }) }}
              />
            )}

            {/* Timestamp + firma */}
            {run.run_status === "ready" && (
              <div className="mt-6 pt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ borderTop: "1px dashed #eae7d8", color: "#94a3b8" }} data-testid="consulting-tool-signature">
                <ShieldCheck className="w-3 h-3" style={{ color: "#C9A84C" }} strokeWidth={2.25} />
                <span>Generado el {new Date(run.updated_at || run.created_at || Date.now()).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}</span>
                <span className="text-slate-300">·</span>
                <span>Metodología OptimizaRRHH</span>
                <span className="text-slate-300">·</span>
                <span>Consultoría OptimizaRRHH</span>
                <span className="text-slate-300">·</span>
                <span className="uppercase tracking-widest font-semibold" style={{ color: "#C9A84C" }}>Confidencial</span>
              </div>
            )}
          </div>
        )}

        {/* Historial */}
        <div
          className="rounded-3xl p-7"
          style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(11,61,46,0.06)" }}
          data-testid="consulting-tool-history"
        >
          <div className="flex items-center gap-3 mb-4">
            <span style={{ width: 24, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
            <History className="w-4 h-4" style={{ color: "#0b3d2e" }} />
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#0b3d2e" }}>Historial</div>
            <div className="text-xs text-slate-500 ml-auto font-semibold">{history.length} generaciones</div>
          </div>
          {loadingHistory ? (
            <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-400 italic">Sin generaciones previas.</p>
          ) : (
            <ul className="space-y-1.5">
              {history.map((h) => (
                <li
                  key={h.run_id}
                  className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm cursor-pointer transition-all ${h.run_id === run?.run_id ? "" : "hover:bg-slate-50"}`}
                  style={h.run_id === run?.run_id ? { background: "linear-gradient(90deg, rgba(15,110,86,0.08) 0%, rgba(201,168,76,0.05) 100%)", boxShadow: "0 0 0 1px rgba(15,110,86,0.15) inset" } : {}}
                  onClick={() => loadRun(h.run_id)}
                  data-testid={`consulting-tool-history-item-${h.run_id}`}
                >
                  <div className="min-w-0 flex items-center gap-2.5">
                    {h.run_id === run?.run_id && <Check className="w-4 h-4 flex-shrink-0" style={{ color: "#0F6E56" }} strokeWidth={2.5} />}
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{h.title || "(sin título)"}</div>
                      <div className="text-[11px] text-slate-500">{new Date(h.created_at).toLocaleString("es-ES")}</div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeRun(h.run_id); }}
                    className="text-slate-400 hover:text-rose-600 p-1 transition-colors"
                    aria-label="Borrar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Modal Share */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setShareOpen(false)}
          data-testid="consulting-tool-share-modal"
        >
          <div
            className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "0 30px 60px -20px rgba(11,61,46,0.45), 0 0 0 1px rgba(201,168,76,0.15)" }}
          >
            <div
              className="relative px-6 py-5 flex items-center justify-between"
              style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
            >
              <span aria-hidden className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#E5C874" }}>Compartir generación</div>
                <div className="text-white font-semibold text-sm mt-0.5 truncate">{run?.title || run?.inputs?.puesto || cfg.title}</div>
              </div>
              <button onClick={() => setShareOpen(false)} className="rounded-full p-1.5 text-white/70 hover:text-white hover:bg-white/10 transition" aria-label="Cerrar" data-testid="consulting-tool-share-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-2">
                  <Link2 className="w-3 h-3" /> Link público (solo-lectura)
                </label>
                {shareLoadingUrl ? (
                  <div className="flex items-center gap-2 text-slate-500 text-sm py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Generando link…
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl}
                      className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs md:text-sm text-slate-700 font-mono"
                      onClick={(e) => e.target.select()}
                      data-testid="consulting-tool-share-url"
                    />
                    <button onClick={copyShareUrl} className="rounded-xl px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 transition" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", color: "#ffffff" }} data-testid="consulting-tool-share-copy">
                      <Copy className="w-3.5 h-3.5" /> Copiar
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-2">
                  Cualquiera con este link podrá ver esta generación. No expone tus notas privadas.
                </p>
              </div>

              <div className="h-px bg-slate-100" />

              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-2">
                  <Mail className="w-3 h-3" /> O envíalo por email (máx 5)
                </label>
                <input
                  type="text"
                  placeholder="ana@empresa.com, socio@empresa.com"
                  value={shareEmails}
                  onChange={(e) => setShareEmails(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-[#0F6E56] focus:ring-2 focus:ring-[#0F6E56]/15"
                  data-testid="consulting-tool-share-emails"
                />
                <textarea
                  placeholder="Mensaje opcional…"
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  rows={2}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-[#0F6E56] focus:ring-2 focus:ring-[#0F6E56]/15 resize-none"
                  data-testid="consulting-tool-share-message"
                />
                <button
                  onClick={sendShareEmail}
                  disabled={shareSending || !shareUrl}
                  className="mt-3 w-full rounded-full py-2.5 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all hover:-translate-y-0.5"
                  style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)", boxShadow: "0 8px 22px -8px rgba(11,61,46,0.5)" }}
                  data-testid="consulting-tool-share-send"
                >
                  {shareSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {shareSending ? "Enviando…" : "Enviar por email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ field, value, onChange }) {
  const cls = "w-full rounded-lg px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/40 transition";
  const inputStyle = { border: "1px solid #e2e8f0" };
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.22em] mb-1.5 block" style={{ color: "#0b3d2e" }}>
        {field.label}{field.required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {field.type === "textarea" ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={field.rows || 3} placeholder={field.placeholder} className={cls} style={inputStyle} data-testid={`consulting-tool-field-${field.name}`} />
      ) : field.type === "select" ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} className={cls} style={inputStyle} data-testid={`consulting-tool-field-${field.name}`}>
          <option value="">— Selecciona —</option>
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : field.type === "checkbox" ? (
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-emerald-600" data-testid={`consulting-tool-field-${field.name}`} />
          {field.placeholder || "Activar"}
        </label>
      ) : (
        <input type={field.type || "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={cls} style={inputStyle} data-testid={`consulting-tool-field-${field.name}`} />
      )}
    </div>
  );
}
